/**
 * O diálogo de **criar** e **editar** Tarefa fora do compositor (11.6, FR-20, FR-80) — no
 * desenho do protótipo v2 desde 28/09/2026 (`formularioDeTarefa` de `p7-modais-eventos.js`).
 *
 * Um componente só para os dois gestos, porque o corpo é o mesmo: o `PUT /tarefas/{id}` recebe a
 * forma **completa** da criação (título, descrição, Tipo, prazo com hora, Responsável e vínculo),
 * e dois formulários divergiriam no primeiro campo novo.
 *
 * **Os campos, na ordem do protótipo:** Assunto; o Tipo em **pílulas** com o ícone (DW-349 —
 * um `radiogroup`, e não um `<select>`); Prazo e Responsável lado a lado, o prazo num
 * `datetime-local` só, que nasce em **amanhã, meio-dia**; o **Negócio** em andamento (opcional —
 * sem Negócio a Tarefa é avulsa); Empresa e Pessoa; a Observação; os Anexos numa área de soltar.
 *
 * - **Criar** (tela de Tarefas): a Tarefa nasce presa a um Negócio, a uma Empresa (e a uma Pessoa
 *   dela) ou a nada. Negócio e Empresa são **exclusivos** no serviço (`400` juntos): com um
 *   Negócio escolhido, a Empresa é a dele e trava — e a Pessoa é das dela.
 * - **Editar** (linha da tela e cartão do histórico): o vínculo **viaja como veio** — o diálogo
 *   não o mostra para troca, e o corpo o repete. Tarefa concluída também se edita (FR-80), mas o
 *   Tipo dela não muda: o Resultado gravado é do Tipo antigo, e o serviço recusa com `409`.
 *
 * **Os Negócios vêm do quadro de cada Funil** (`useNegociosEmAndamento`): não há `GET /negocios`
 * sem recorte, e o quadro é justamente "os em andamento que a pessoa enxerga" — nas mesmas chaves
 * de cache do Funil. A leitura só sai com o diálogo montado (DW-299).
 *
 * **Quem pode é o serviço.** O botão aparece para todo mundo que vê a linha; a policy e a guarda
 * de `editar_tarefa` respondem `403` a quem não é Responsável, criador ou gestão, e a mensagem
 * chega verbatim pelo `Dialogo` (esconder botão por perfil é conveniência, não autorização).
 *
 * **Os anexos sobem depois**, um a um, pela rota da Tarefa (AD-9): a Tarefa já existe quando eles
 * saem, e a falha de um upload não a desfaz — o aviso diz as duas coisas. Cada arquivo é conferido
 * **antes** de a Tarefa nascer (tipo, vazio, teto), para o recusado não deixar uma Tarefa sem ele.
 */

import {
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import {
  useEmpresas,
  useNomesDeEmpresas,
  useNomesDePessoas,
  usePessoas,
} from '@/modulos/cadastro/api'
import { nomeExibidoDaEmpresa } from '@/modulos/cadastro/nome-da-empresa'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { Dialogo } from './Dialogo'
import { IconeDoTipo } from './IconeDoTipo'
import {
  type NegocioEmAndamento,
  type Tarefa,
  useAnexarArquivo,
  useCriarTarefa,
  useEditarTarefa,
  useNegociosEmAndamento,
  useTiposDeTarefa,
} from './api'
import {
  EMPRESA_A_DEFINIR,
  TAMANHO_DA_DESCRICAO,
  TAMANHO_DO_TITULO,
  instanteDoPrazoLocal,
  prazoPadraoDaTarefa,
  valorLocalDoPrazo,
} from './apresentacao'
import { anunciar } from './avisos'
import {
  ACEITA_NO_ANEXO,
  EQUIPE_INDISPONIVEL,
  type EstadoDoCatalogo,
  TAMANHO_MAXIMO_DO_ANEXO,
  recusaDoAnexo,
  recusaLocal,
  rotuloDoResponsavel,
  tipoDerivado,
} from './regras-de-tarefa'
import './ficha-do-negocio.css'
import './tarefas.css'

export const TITULO_DA_CRIACAO_AVULSA = 'Nova tarefa'
/** O subtítulo do protótipo v2 (`modais['nova-tarefa']`). */
export const SUBTITULO_DA_CRIACAO_AVULSA = 'Pode ficar presa a um negócio ou solta.'
export const TITULO_DA_EDICAO = 'Editar tarefa'
export const SUBTITULO_DA_EDICAO = 'O vínculo da tarefa continua o mesmo.'
export const SALVAR_TAREFA = 'Salvar'
export const CRIAR_TAREFA_AVULSA = 'Criar tarefa'
export const TAREFA_CRIADA_AVULSA = 'Tarefa criada.'
export const TAREFA_SALVA = 'Tarefa salva.'
export const ANEXO_NAO_SUBIU_NA_CRIACAO = 'Tarefa criada, mas o anexo não foi enviado:'
export const ANEXO_NAO_SUBIU_NA_EDICAO = 'Tarefa salva, mas o anexo não foi enviado:'
export const TIPO_TRAVADO_NA_CONCLUIDA =
  'O tipo de uma tarefa concluída não muda: o resultado gravado é do tipo dela.'
export const SEM_EMPRESA = 'Sem empresa'
export const SEM_PESSOA = 'Sem pessoa'
/** A primeira `<option>` do Negócio (protótipo v2). */
export const SEM_VINCULO = 'Sem vínculo'
export const NEGOCIOS_INDISPONIVEIS =
  'Não foi possível carregar os negócios. A tarefa ainda pode nascer avulsa ou presa a uma empresa.'

/** Os rótulos dos campos, com as palavras do protótipo v2 — a suíte os encontra por eles. */
export const ROTULOS_DO_DIALOGO = {
  assunto: 'Assunto',
  tipo: 'Tipo',
  prazo: 'Prazo',
  responsavel: 'Responsável',
  negocio: 'Negócio (opcional — sem negócio a tarefa é avulsa)',
  buscaDaEmpresa: 'Buscar empresa',
  empresa: 'Empresa (opcional)',
  pessoa: 'Pessoa (opcional)',
  observacao: 'Observação',
  anexos: 'Anexos',
} as const

/** As duas linhas da área de soltar — a segunda diz o que o serviço aceita, e o teto dele. */
export const SOLTAR_ANEXOS_AQUI = 'Arraste arquivos ou imagens aqui, ou '
export const CLIQUE_PARA_ESCOLHER = 'clique para escolher'
/** O nome da lista dos arquivos escolhidos — distinto do rótulo do campo, que é "Anexos". */
export const ANEXOS_ESCOLHIDOS = 'Anexos escolhidos'
export const O_QUE_O_ANEXO_ACEITA = `PDF, imagens, documentos, planilhas · até ${String(
  TAMANHO_MAXIMO_DO_ANEXO / (1024 * 1024),
)} MB cada`

const ID = {
  titulo: 'dialogo-tarefa-titulo',
  descricao: 'dialogo-tarefa-descricao',
  tipo: 'dialogo-tarefa-tipo',
  prazo: 'dialogo-tarefa-prazo',
  responsavel: 'dialogo-tarefa-responsavel',
  negocio: 'dialogo-tarefa-negocio',
  buscaDaEmpresa: 'dialogo-tarefa-busca-da-empresa',
  empresa: 'dialogo-tarefa-empresa',
  pessoa: 'dialogo-tarefa-pessoa',
  anexo: 'dialogo-tarefa-anexo',
  rotuloDosAnexos: 'dialogo-tarefa-anexos-rotulo',
} as const

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

export interface PropriedadesDoDialogoDaTarefa {
  /** Ausente: criação avulsa. Presente: edição daquela Tarefa. */
  tarefa?: Tarefa
  aoFechar: () => void
}

export function DialogoDaTarefa({ tarefa, aoFechar }: PropriedadesDoDialogoDaTarefa) {
  const editando = tarefa !== undefined
  const { data: sessao } = useSessao()
  const catalogo = useTiposDeTarefa()
  const equipe = useResponsaveis()
  const criacao = useCriarTarefa()
  const edicao = useEditarTarefa()
  //: O upload pela mutação: ela relê as Tarefas depois (a lista passa a mostrar o anexo).
  const upload = useAnexarArquivo()
  const mutacao = editando ? edicao : criacao

  //: O prazo como o `datetime-local` o carrega: o gravado, na edição; amanhã ao meio-dia, na
  //: criação (protótipo v2). Calculado uma vez — o relógio não muda o campo no meio da digitação.
  const [prazoInicial] = useState(() =>
    tarefa === undefined ? prazoPadraoDaTarefa() : valorLocalDoPrazo(tarefa.prazo),
  )
  const [titulo, setTitulo] = useState(tarefa?.titulo ?? '')
  const [descricao, setDescricao] = useState(tarefa?.descricao ?? '')
  const [tipo, setTipo] = useState(tarefa?.tipo_de_tarefa_id ?? '')
  const [prazo, setPrazo] = useState(prazoInicial)
  const [responsavel, setResponsavel] = useState(tarefa?.responsavel_id ?? '')
  const [negocio, setNegocio] = useState('')
  const [buscaDaEmpresa, setBuscaDaEmpresa] = useState('')
  const [empresa, setEmpresa] = useState('')
  const [nomeDaEmpresa, setNomeDaEmpresa] = useState('')
  const [pessoa, setPessoa] = useState('')
  const [anexos, setAnexos] = useState<File[]>([])
  const [arrastando, setArrastando] = useState(false)
  const [recusa, setRecusa] = useState<string | null>(null)
  const [enviandoAnexo, setEnviandoAnexo] = useState(false)

  const campoDoTitulo = useRef<HTMLInputElement>(null)
  useEffect(() => {
    campoDoTitulo.current?.focus()
  }, [])

  /* Os Negócios em andamento e o nome das Empresas deles — só na criação (na edição o vínculo
     não se troca), e só com o diálogo montado. */
  const negocios = useNegociosEmAndamento({ habilitada: !editando })
  const idsDasEmpresasDosNegocios = useMemo(
    () => [
      ...new Set(
        negocios.negocios.flatMap((cada) => (cada.empresa_id === null ? [] : [cada.empresa_id])),
      ),
    ],
    [negocios.negocios],
  )
  const nomesDasEmpresas = useNomesDeEmpresas(idsDasEmpresasDosNegocios)
  /* 13.20b2: o contato dos Negócios sem Empresa, num lote do `cadastro` (AD-25) — o nome na opção
     e a Pessoa que o diálogo oferece como contato da Tarefa. */
  const idsDosContatosDosNegocios = useMemo(
    () => [
      ...new Set(
        negocios.negocios.flatMap((cada) =>
          cada.empresa_id === null && cada.pessoa_id !== null ? [cada.pessoa_id] : [],
        ),
      ),
    ],
    [negocios.negocios],
  )
  const nomesDosContatos = useNomesDePessoas(idsDosContatosDosNegocios)
  const negocioEscolhido: NegocioEmAndamento | undefined =
    negocio === '' ? undefined : negocios.negocios.find((cada) => cada.id === negocio)
  //: A Empresa do vínculo: a do Negócio, quando há um; a escolhida, senão.
  //: 13.20b2 (code review, B9): o N0 escolhido não herda a Empresa digitada antes — ele não tem
  //: nenhuma, e as Pessoas dela não são dele.
  const empresaDoVinculo =
    negocioEscolhido === undefined ? empresa : (negocioEscolhido.empresa_id ?? '')
  //: 13.20b2: o Negócio sem Empresa escolhido oferece o contato dele — só quando quem cria o vê
  //: (o nome veio no lote); sem ele, nenhum nome é inventado.
  const contatoDoNegocioSemEmpresa =
    negocioEscolhido !== undefined &&
    negocioEscolhido.empresa_id === null &&
    negocioEscolhido.pessoa_id !== null &&
    nomesDosContatos.nomes.has(negocioEscolhido.pessoa_id)
      ? {
          id: negocioEscolhido.pessoa_id,
          nome: nomesDosContatos.nomes.get(negocioEscolhido.pessoa_id) ?? '',
        }
      : undefined
  const pessoas = usePessoas(editando ? '' : empresaDoVinculo)

  const tipos = useMemo(() => catalogo.data ?? [], [catalogo.data])
  const concluida = tarefa?.concluida_em != null
  //: Na edição o Tipo gravado vale mesmo fora do catálogo (Tipo excluído, FR-69): a pílula
  //: sintética o mantém, e o corpo o repete. Na criação a escolha cai no primeiro ativo.
  const tipoEscolhido = editando && tipo === tarefa.tipo_de_tarefa_id ? tipo : tipoDerivado(tipo, tipos)
  const tipoForaDoCatalogo = editando && !tipos.some((cada) => cada.id === tarefa.tipo_de_tarefa_id)
  const pilulas = useMemo(
    () =>
      tipoForaDoCatalogo
        ? [{ id: tarefa.tipo_de_tarefa_id, nome: tarefa.tipo_nome, icone: null }, ...tipos]
        : tipos,
    [tipoForaDoCatalogo, tarefa, tipos],
  )

  const daEquipe = equipe.data ?? []
  const escolhiveis = daEquipe.filter((cada) => cada.ativo)
  const responsavelEscolhido = responsavel !== '' ? responsavel : (sessao?.usuario_id ?? '')
  const responsavelSemRotulo =
    responsavelEscolhido !== '' && !escolhiveis.some((cada) => cada.id === responsavelEscolhido)

  const estadoDoCatalogo: EstadoDoCatalogo = catalogo.isError
    ? 'indisponivel'
    : catalogo.isLoading
      ? 'carregando'
      : 'pronto'

  const ocupado = mutacao.isPending || enviandoAnexo

  function limparOsAvisos() {
    setRecusa(null)
    if (!mutacao.isPending) {
      mutacao.reset()
    }
  }

  /** Junta os arquivos escolhidos aos que já estão na lista — sem repetir o mesmo nome. */
  function acrescentarAnexos(lista: FileList | File[] | null | undefined) {
    if (lista === null || lista === undefined) {
      return
    }
    limparOsAvisos()
    setAnexos((atuais) => {
      const nomes = new Set(atuais.map((cada) => cada.name))
      return [...atuais, ...[...lista].filter((cada) => !nomes.has(cada.name))]
    })
  }

  function soltar(evento: DragEvent<HTMLElement>) {
    evento.preventDefault()
    setArrastando(false)
    acrescentarAnexos(evento.dataTransfer.files)
  }

  /** O Tipo pelas setas, como num grupo de rádio: esquerda/direita andam pela lista. */
  function tipoPelaTecla(evento: KeyboardEvent<HTMLDivElement>) {
    if (concluida || pilulas.length === 0) {
      return
    }
    const passo = evento.key === 'ArrowRight' || evento.key === 'ArrowDown' ? 1 : evento.key === 'ArrowLeft' || evento.key === 'ArrowUp' ? -1 : 0
    if (passo === 0) {
      return
    }
    evento.preventDefault()
    const atual = pilulas.findIndex((cada) => cada.id === tipoEscolhido)
    const proximo = pilulas[(atual + passo + pilulas.length) % pilulas.length]
    if (proximo !== undefined) {
      limparOsAvisos()
      setTipo(proximo.id)
      document.getElementById(`${ID.tipo}-${proximo.id}`)?.focus()
    }
  }

  async function subirOsAnexos(tarefaId: string, prefixo: string, feito: string) {
    if (anexos.length === 0) {
      anunciar(feito)
      aoFechar()
      return
    }
    setEnviandoAnexo(true)
    const falhas: string[] = []
    try {
      for (const arquivo of anexos) {
        try {
          await upload.mutateAsync({ tarefaId, arquivo })
        } catch (falha) {
          falhas.push(mensagemDe(falha))
        }
      }
      anunciar(falhas.length === 0 ? feito : `${prefixo} ${falhas.join(' ')}`)
    } finally {
      setEnviandoAnexo(false)
      aoFechar()
    }
  }

  function enviar(evento?: FormEvent) {
    evento?.preventDefault()
    if (ocupado) {
      return
    }
    //: Os anexos são conferidos **antes** de a Tarefa nascer: recusados depois, a Tarefa já
    //: existiria sem eles.
    const local =
      recusaLocal({
        titulo,
        prazo,
        tipoEscolhido,
        responsavelEscolhido,
        catalogo: editando && tipoForaDoCatalogo ? 'pronto' : estadoDoCatalogo,
      }) ??
      anexos.map(recusaDoAnexo).find((cada) => cada !== null) ??
      null
    setRecusa(local)
    if (local !== null) {
      return
    }
    //: Na edição, o prazo intocado devolve o instante **como estava** — reescrevê-lo pelo
    //: formulário trocaria o fuso ou os segundos em silêncio (e a trilha acusaria uma mudança que
    //: ninguém fez).
    const prazoIntocado = tarefa !== undefined && prazo === prazoInicial
    const corpo = {
      titulo: titulo.trim(),
      descricao: descricao.trim() === '' ? null : descricao.trim(),
      tipo_de_tarefa_id: tipoEscolhido,
      responsavel_id: responsavelEscolhido,
      prazo: prazoIntocado ? tarefa.prazo : instanteDoPrazoLocal(prazo),
    }
    if (tarefa !== undefined) {
      edicao.mutate(
        {
          tarefaId: tarefa.id,
          edicao: {
            ...corpo,
            negocio_id: tarefa.negocio_id,
            empresa_id: tarefa.empresa_id,
            pessoa_id: tarefa.pessoa_id,
          },
        },
        {
          onSuccess: () => {
            void subirOsAnexos(tarefa.id, ANEXO_NAO_SUBIU_NA_EDICAO, TAREFA_SALVA)
          },
        },
      )
      return
    }
    //: Negócio e Empresa são exclusivos no serviço: com Negócio, a Empresa vai `null` (ela é a
    //: dele, e o serviço a deriva); a Pessoa acompanha o vínculo que houver.
    criacao.mutate(
      {
        ...corpo,
        negocio_id: negocio === '' ? null : negocio,
        empresa_id: negocio !== '' || empresa === '' ? null : empresa,
        pessoa_id: pessoa === '' ? null : pessoa,
      },
      {
        onSuccess: (criada) => {
          void subirOsAnexos(criada.id, ANEXO_NAO_SUBIU_NA_CRIACAO, TAREFA_CRIADA_AVULSA)
        },
      },
    )
  }

  const nomeDaEmpresaDoNegocio =
    negocioEscolhido === undefined
      ? ''
      : negocioEscolhido.empresa_id === null
        ? EMPRESA_A_DEFINIR
        : (nomesDasEmpresas.nomes.get(negocioEscolhido.empresa_id) ?? '…')
  /** A opção de um Negócio: o código e a Empresa — ou, sem Empresa, o contato e "Empresa a definir". */
  const rotuloDaOpcao = (cada: NegocioEmAndamento): string => {
    if (cada.empresa_id === null) {
      const contato =
        cada.pessoa_id === null ? undefined : nomesDosContatos.nomes.get(cada.pessoa_id)
      return [String(cada.codigo), contato, EMPRESA_A_DEFINIR].filter(Boolean).join(' · ')
    }
    return `${String(cada.codigo)} · ${nomesDasEmpresas.nomes.get(cada.empresa_id) ?? '…'}`
  }

  return (
    <Dialogo
      titulo={editando ? TITULO_DA_EDICAO : TITULO_DA_CRIACAO_AVULSA}
      subtitulo={editando ? SUBTITULO_DA_EDICAO : SUBTITULO_DA_CRIACAO_AVULSA}
      rotuloDoConfirmar={editando ? SALVAR_TAREFA : CRIAR_TAREFA_AVULSA}
      ocupado={ocupado}
      erro={mutacao.isError ? mutacao.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        enviar()
      }}
    >
      <form className="nova-tarefa nova-tarefa--grade" onSubmit={enviar}>
        <button type="submit" className="nova-tarefa__submit" aria-hidden="true" tabIndex={-1} />

        <div className="nova-tarefa__campo nova-tarefa__campo--largo">
          <label className="ficha__linha-rotulo" htmlFor={ID.titulo}>
            {ROTULOS_DO_DIALOGO.assunto}
          </label>
          <input
            id={ID.titulo}
            ref={campoDoTitulo}
            className="ficha__entrada"
            type="text"
            placeholder="ex.: Ligar para confirmar amostra"
            value={titulo}
            maxLength={TAMANHO_DO_TITULO}
            onChange={(evento) => {
              limparOsAvisos()
              setTitulo(evento.target.value)
            }}
          />
        </div>

        <div className="nova-tarefa__campo nova-tarefa__campo--largo">
          <span className="ficha__linha-rotulo" id={ID.tipo}>
            {ROTULOS_DO_DIALOGO.tipo}
          </span>
          {/* As pílulas do protótipo (`.paleta` + `.cor-opcao`): um grupo de rádio, o ícone ao
              lado do nome — o ícone nunca é o único sinal. */}
          <div
            className="nova-tarefa__pilulas"
            role="radiogroup"
            aria-labelledby={ID.tipo}
            aria-describedby={concluida ? `${ID.tipo}-travado` : undefined}
            onKeyDown={tipoPelaTecla}
          >
            {pilulas.map((cada) => {
              const marcada = cada.id === tipoEscolhido
              return (
                <button
                  key={cada.id}
                  id={`${ID.tipo}-${cada.id}`}
                  type="button"
                  role="radio"
                  aria-checked={marcada}
                  tabIndex={marcada ? 0 : -1}
                  className="nova-tarefa__pilula"
                  disabled={concluida}
                  onClick={() => {
                    limparOsAvisos()
                    setTipo(cada.id)
                  }}
                >
                  {cada.icone ? (
                    <span className="nova-tarefa__pilula-icone" aria-hidden="true">
                      <IconeDoTipo icone={cada.icone} />
                    </span>
                  ) : null}
                  {cada.nome}
                </button>
              )
            })}
          </div>
          {concluida ? (
            <span id={`${ID.tipo}-travado`} className="tela-tarefas__dica">
              {TIPO_TRAVADO_NA_CONCLUIDA}
            </span>
          ) : null}
        </div>

        <div className="nova-tarefa__campo">
          <label className="ficha__linha-rotulo" htmlFor={ID.prazo}>
            {ROTULOS_DO_DIALOGO.prazo}
          </label>
          <input
            id={ID.prazo}
            className="ficha__entrada"
            type="datetime-local"
            value={prazo}
            onChange={(evento) => {
              limparOsAvisos()
              setPrazo(evento.target.value)
            }}
          />
        </div>

        <div className="nova-tarefa__campo">
          <label className="ficha__linha-rotulo" htmlFor={ID.responsavel}>
            {ROTULOS_DO_DIALOGO.responsavel}
          </label>
          <select
            id={ID.responsavel}
            className="ficha__selecao"
            value={responsavelEscolhido}
            onChange={(evento) => {
              limparOsAvisos()
              setResponsavel(evento.target.value)
            }}
          >
            {responsavelSemRotulo ? (
              <option value={responsavelEscolhido}>
                {rotuloDoResponsavel(responsavelEscolhido, { sessao, daEquipe })}
              </option>
            ) : null}
            {escolhiveis.map((cada) => (
              <option key={cada.id} value={cada.id}>
                {cada.nome}
              </option>
            ))}
          </select>
        </div>

        {editando ? null : (
          <>
            <div className="nova-tarefa__campo nova-tarefa__campo--largo">
              <label className="ficha__linha-rotulo" htmlFor={ID.negocio}>
                {ROTULOS_DO_DIALOGO.negocio}
              </label>
              <select
                id={ID.negocio}
                className="ficha__selecao"
                value={negocio}
                aria-busy={negocios.carregando || undefined}
                onChange={(evento) => {
                  limparOsAvisos()
                  setNegocio(evento.target.value)
                  setPessoa('')
                }}
              >
                <option value="">{SEM_VINCULO}</option>
                {negocios.negocios.map((cada) => (
                  <option key={cada.id} value={cada.id}>
                    {rotuloDaOpcao(cada)}
                  </option>
                ))}
              </select>
              {negocios.falhou ? (
                <span className="tela-tarefas__dica">{NEGOCIOS_INDISPONIVEIS}</span>
              ) : null}
            </div>

            <div className="nova-tarefa__campo">
              <label className="ficha__linha-rotulo" htmlFor={ID.empresa}>
                {ROTULOS_DO_DIALOGO.empresa}
              </label>
              {negocioEscolhido === undefined ? (
                <>
                  <input
                    id={ID.buscaDaEmpresa}
                    className="ficha__entrada"
                    type="search"
                    aria-label={ROTULOS_DO_DIALOGO.buscaDaEmpresa}
                    placeholder="Buscar por nome"
                    value={buscaDaEmpresa}
                    onChange={(evento) => {
                      limparOsAvisos()
                      setBuscaDaEmpresa(evento.target.value)
                    }}
                  />
                  <SeletorDeEmpresa
                    termo={buscaDaEmpresa.trim()}
                    empresa={empresa}
                    nomeDaEmpresa={nomeDaEmpresa}
                    aoEscolher={(id, nome) => {
                      limparOsAvisos()
                      setEmpresa(id)
                      setNomeDaEmpresa(nome)
                      setPessoa('')
                    }}
                  />
                </>
              ) : (
                /* Com Negócio, a Empresa é a dele: o seletor mostra qual e trava. */
                <select id={ID.empresa} className="ficha__selecao" value="negocio" disabled>
                  <option value="negocio">{nomeDaEmpresaDoNegocio}</option>
                </select>
              )}
            </div>

            <div className="nova-tarefa__campo">
              <label className="ficha__linha-rotulo" htmlFor={ID.pessoa}>
                {ROTULOS_DO_DIALOGO.pessoa}
              </label>
              <select
                id={ID.pessoa}
                className="ficha__selecao"
                value={pessoa}
                disabled={empresaDoVinculo === '' && contatoDoNegocioSemEmpresa === undefined}
                onChange={(evento) => {
                  limparOsAvisos()
                  setPessoa(evento.target.value)
                }}
              >
                <option value="">{SEM_PESSOA}</option>
                {contatoDoNegocioSemEmpresa === undefined ? null : (
                  <option value={contatoDoNegocioSemEmpresa.id}>
                    {contatoDoNegocioSemEmpresa.nome}
                  </option>
                )}
                {(empresaDoVinculo === '' ? [] : (pessoas.data ?? [])).map((cada) => (
                  <option key={cada.id} value={cada.id}>
                    {cada.nome}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="nova-tarefa__campo nova-tarefa__campo--largo">
          <label className="ficha__linha-rotulo" htmlFor={ID.descricao}>
            {ROTULOS_DO_DIALOGO.observacao}
          </label>
          <textarea
            id={ID.descricao}
            className="ficha__entrada ficha__entrada--longa nova-tarefa__observacao"
            rows={2}
            value={descricao}
            maxLength={TAMANHO_DA_DESCRICAO}
            onChange={(evento) => {
              limparOsAvisos()
              setDescricao(evento.target.value)
            }}
          />
        </div>

        <div className="nova-tarefa__campo nova-tarefa__campo--largo">
          <span className="ficha__linha-rotulo" id={ID.rotuloDosAnexos}>
            {ROTULOS_DO_DIALOGO.anexos}
          </span>
          {/* A área de soltar do protótipo (`.solta`): é um `<label>` do campo de arquivo, e por
              isso o clique abre o seletor sem JavaScript; o arrastar cai no `onDrop`. */}
          <label
            htmlFor={ID.anexo}
            className="nova-tarefa__solta"
            data-arrastando={arrastando || undefined}
            onDragOver={(evento) => {
              evento.preventDefault()
              setArrastando(true)
            }}
            onDragLeave={() => {
              setArrastando(false)
            }}
            onDrop={soltar}
          >
            <span>
              {SOLTAR_ANEXOS_AQUI}
              <b>{CLIQUE_PARA_ESCOLHER}</b>
            </span>
            <span className="nova-tarefa__solta-dica">{O_QUE_O_ANEXO_ACEITA}</span>
          </label>
          <input
            id={ID.anexo}
            className="nova-tarefa__arquivo"
            type="file"
            multiple
            accept={ACEITA_NO_ANEXO}
            aria-labelledby={ID.rotuloDosAnexos}
            onChange={(evento) => {
              acrescentarAnexos(evento.target.files)
              //: O mesmo arquivo pode ser escolhido de novo depois de removido da lista.
              evento.target.value = ''
            }}
          />
          {anexos.length === 0 ? null : (
            <ul className="nova-tarefa__anexos" aria-label={ANEXOS_ESCOLHIDOS}>
              {anexos.map((arquivo) => (
                <li key={arquivo.name} className="nova-tarefa__anexo">
                  <span aria-hidden="true">📎 </span>
                  {arquivo.name}
                  <button
                    type="button"
                    className="nova-tarefa__remover-anexo"
                    aria-label={`Remover ${arquivo.name}`}
                    onClick={() => {
                      limparOsAvisos()
                      setAnexos((atuais) => atuais.filter((cada) => cada !== arquivo))
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {recusa !== null ? (
          <p className="ficha__erro nova-tarefa__campo--largo" role="alert">
            {recusa}
          </p>
        ) : null}
        {equipe.isError ? (
          <p className="ficha__erro nova-tarefa__campo--largo" role="alert">
            {EQUIPE_INDISPONIVEL}
          </p>
        ) : null}
      </form>
    </Dialogo>
  )
}

/** Com menos de duas letras a busca não sai — e o `GET /empresas` nem é montado. */
const MINIMO_DA_BUSCA = 2

interface PropriedadesDoSeletorDeEmpresa {
  termo: string
  empresa: string
  nomeDaEmpresa: string
  aoEscolher: (id: string, nome: string) => void
}

/**
 * O `<select>` da Empresa, alimentado pela busca do `cadastro` (a policy de lá decide o que a
 * pessoa enxerga, FR-30). A escolhida continua como `<option>` mesmo quando a busca muda.
 */
function SeletorDeEmpresa(propriedades: PropriedadesDoSeletorDeEmpresa) {
  if (propriedades.termo.length < MINIMO_DA_BUSCA) {
    return <SelecaoDaEmpresa {...propriedades} opcoes={[]} />
  }
  return <SeletorDeEmpresaComBusca {...propriedades} />
}

function SeletorDeEmpresaComBusca(propriedades: PropriedadesDoSeletorDeEmpresa) {
  const busca = useEmpresas({ busca: propriedades.termo, limite: 20 })
  const opcoes = (busca.data?.itens ?? []).map((cada) => ({
    id: cada.id,
    nome: nomeExibidoDaEmpresa(cada),
  }))
  return <SelecaoDaEmpresa {...propriedades} opcoes={opcoes} />
}

function SelecaoDaEmpresa({
  empresa,
  nomeDaEmpresa,
  aoEscolher,
  opcoes,
}: PropriedadesDoSeletorDeEmpresa & { opcoes: { id: string; nome: string }[] }) {
  const escolhidaForaDaLista = empresa !== '' && !opcoes.some((cada) => cada.id === empresa)
  return (
    <select
      id={ID.empresa}
      className="ficha__selecao"
      value={empresa}
      onChange={(evento) => {
        const id = evento.target.value
        aoEscolher(id, opcoes.find((cada) => cada.id === id)?.nome ?? '')
      }}
    >
      <option value="">{SEM_EMPRESA}</option>
      {escolhidaForaDaLista ? <option value={empresa}>{nomeDaEmpresa}</option> : null}
      {opcoes.map((cada) => (
        <option key={cada.id} value={cada.id}>
          {cada.nome}
        </option>
      ))}
    </select>
  )
}
